import { isString } from 'lodash';

import type { Maybe } from './globals';

/**
 * Comet check-in language tags (values are what the portal persists, e.g. `es-MX`, `pt-BR`).
 * Keys are primary-language codes Neutron uses for greeting / template resolution.
 * Incoming tags are matched via the primary language subtag (`split('-')[0]`), except
 * Traditional Chinese (`zh-TW`, `zh-HK`, `zh-MO`, `zh-Hant*`), which resolves to `zh_tw`.
 */
export const DRIVER_GREETING_COMET_LOCALES = {
  en: 'en', // English
  es: 'es-MX', // Spanish (Mexico)
  fr: 'fr', // French
  pt: 'pt-BR', // Portuguese (Brazil)
  ar: 'ar', // Arabic
  pl: 'pl', // Polish
  ru: 'ru', // Russian
  uk: 'uk', // Ukrainian
  hi: 'hi', // Hindi
  zh: 'zh', // Mandarin (Simplified Chinese)
  zh_tw: 'zh-TW', // Traditional Chinese
  ja: 'ja', // Japanese
  ko: 'ko', // Korean
  id: 'id', // Bahasa Indonesia
  th: 'th', // Thai
  vi: 'vi', // Vietnamese
  ms: 'ms', // Malay
  pa: 'pa' // Punjabi (Gurmukhi)
} as const;

export type DriverGreetingCometLocale = keyof typeof DRIVER_GREETING_COMET_LOCALES;

/**
 * Canonical (instruction) stop-translate keyword per Comet locale.
 * Typo / alternate forms live only in {@link STOP_TRANSLATE_KEYWORDS} for matching.
 */
export const CANONICAL_STOP_TRANSLATE_KEYWORDS: Record<DriverGreetingCometLocale, string> = {
  en: 'STOP TRANSLATION',
  es: 'DETENER TRADUCCIÓN',
  fr: 'ARRÊTER TRADUCTION',
  pt: 'PARAR TRADUÇÃO',
  ar: 'إيقاف الترجمة',
  pl: 'ZATRZYMAJ TŁUMACZENIE',
  ru: 'СТОП ПЕРЕВОД',
  uk: 'СТОП ПЕРЕКЛАД',
  hi: 'अनुवाद बंद',
  zh: '停止翻译',
  zh_tw: '停止翻譯',
  ja: '翻訳停止',
  ko: '번역 중지',
  id: 'HENTIKAN TERJEMAHAN',
  th: 'หยุดแปล',
  vi: 'DỪNG DỊCH',
  ms: 'HENTIKAN TERJEMAHAN',
  pa: 'ਅਨੁਵਾਦ ਬੰਦ'
};

export const STOP_TRANSLATE_KEYWORDS: readonly string[] = [
  // English
  'STOP TRANSLATION',
  'STOP TRASLATION',
  'STOP TRANSLATE',
  'STOP TRASLATE',

  // Spanish
  'DETENER TRADUCCIÓN',
  'DETENER TRADUCCION',
  'DETENER TRADUCION',
  'DETENER TRADDUCCION',
  'DETENER TRADUCTION',
  'DETENER TRADUC',
  'DEETENER TRADUCCION',
  'DETNER TRADUCCION',
  'PARAR TRADUCCIÓN',
  'PARAR TRADUCCION',
  'PARAR TRADUCION',
  'PARAR TRADUC',
  'STOP TRADUCCIÓN',
  'STOP TRADUCCION',

  // French
  'ARRÊTER TRADUCTION',
  'ARRETER TRADUCTION',
  'ARETER TRADUCTION',
  'ARRÉTER TRADUCTION',
  'ARRETER TRADUCTOIN',
  'ARRETER TRADUCTON',
  'ARRETER TRADUTION',
  'ARRÊTER TRADUCTON',
  'ARRETE TRADUCTION',
  'ARRETEZ TRADUCTION',
  'CESSER TRADUCTION',
  'CESSER LA TRADUCTION',
  'ARRETER TRADUC',
  'STOP TRADUCTION',

  // Brazilian Portuguese
  'PARAR TRADUÇÃO',
  'PARAR TRADUCAO',
  'PARAR TRADUÇAO',
  'PARAR TRADUCÃO',
  'PARAR TRADUCA0',
  'PARAR TRADUZAO',
  'PARA TRADUÇÃO',
  'PARA TRADUCAO',
  'PARAR TRAD',
  'PARAR A TRADUÇÃO',
  'PARAR A TRADUCAO',

  // Arabic + romanized
  'إيقاف الترجمة',
  'ايقاف الترجمة',
  'ايقاف الترجمه',
  'إيقاف الترجمه',
  'ايقافترجمه',
  'وقف الترجمة',
  'وقف الترجمه',
  'ايقاف الترجم',
  'EIQAF ALTARJAMA',
  'EIQAF AL TARJAMA',
  'IQAF ALTARJAMA',
  'IQAF AL TARJAMA',
  'WAQAF ALTARJAMA',
  'STOP TARJAMA',
  'STOP AL TARJAMA',

  // Polish + romanized / typo variants
  'ZATRZYMAJ TŁUMACZENIE',
  'ZATRZYMAJ TLUMACZENIE',
  'ZATRZYMAJ TŁUMACZENI',
  'ZATRZYMAJ TLUMACZENI',
  'ZATRZYMAĆ TŁUMACZENIE',
  'ZATRZYMAC TLUMACZENIE',
  'STOP TŁUMACZENIE',
  'STOP TLUMACZENIE',
  'WYŁĄCZ TŁUMACZENIE',
  'WYLACZ TLUMACZENIE',
  'WYLACZ TŁUMACZENIE',

  // Russian Cyrillic + romanized
  'СТОП ПЕРЕВОД',
  'СТОП ПЕРЕВОДИ',
  'СТОП ПЕРЕВОДТ',
  'СТОП ПЕЕРВОД',
  'СТОП ПЕРЕВДО',
  'СТОП ПЕРЕВОТ',
  'СТОП ПЕРЕОД',
  'СТОП ПЕРЕВ',
  'ВЫКЛ ПЕРЕВОД',
  'ОТКЛ ПЕРЕВОД',
  'ОТКЛЮЧИТЬ ПЕРЕВОД',
  'STOP PEREVOD',
  'STOP PEREOVD',
  'STOP PEREVOT',
  'STOP PERVOD',
  'VYKL PEREVOD',
  'OTKL PEREVOD',

  // Ukrainian Cyrillic + romanized
  'СТОП ПЕРЕКЛАД',
  'СТОП ПЕРЕКЛА',
  'СТОП ПЕРЕКДАД',
  'СТОП ПЕРЕКАЛД',
  'СТОП ПИРЕКЛА',
  'СТОП ПЕРЕКЛ',
  'ЗУПИНИТИ ПЕРЕКЛАД',
  'ЗУПИНИ ПЕРЕКЛАД',
  'ЗУПИН ПЕРЕКЛАД',
  'ВИКЛ ПЕРЕКЛАД',
  'ВІДКЛ ПЕРЕКЛАД',
  'STOP PEREKLAD',
  'STOP PEREKLA',
  'ZUPYNYTY PEREKLAD',
  'ZUPYNY PEREKLAD',
  'VIKL PEREKLAD',

  // Punjabi Gurmukhi + romanized
  'ਅਨੁਵਾਦ ਬੰਦ',
  'ਅਨੁਵਾਦ ਬਨਦ',
  'ਅਨੁਵਾਦਬੰਦ',
  'ਅਨੁਵਾਦ ਬੰਦ ਕਰੋ',
  'ਅਨੁਵਾਦ ਰੋਕੋ',
  'ਤਰਜਮਾ ਬੰਦ',
  'ਤਰਜਮਾ ਬੰਦ ਕਰੋ',
  'TARJAMA BAND',
  'TARJUMA BAND',
  'TARJMA BAND',
  'ANUVAD BAND KARO',
  'ANUVAAD BAND KARO',
  'ANUVAD BAND',
  'ANUWAD BAND',
  'ANUVAAD BAND',
  'ANUAD BAND',
  'ANUVAD BUND',
  'ANUVAD BND',
  'ANUVAD BHAND',
  'ANUVAD BEND',
  'ANUVAD BAAND',
  'ANUVAD BANDH',

  // Hindi Devanagari + romanized
  'अनुवाद बंद',
  'अनुवाद रोकें',
  'अनुवाद रोको',
  'अनुवाद रोके',
  'अनुवादरोकें',
  'ANUVAD ROKEIN',
  'ANUVAD ROKEN',
  'ANUVAD ROKAIN',
  'ANUVAD ROKE',
  'ANUWAD ROKEIN',
  'ANUVAAD ROKEIN',
  'ANUVAD ROKHEIN',
  'ANUVAD ROKIEN',
  'ANUVAD ROKNE',

  // Tagalog
  'ITIGIL PAGSASALIN',
  'ITIGIL PAGSALIN',
  'ITIGIL PAGSASLIN',
  'ITIGIL PAGASALIN',
  'ITIGIL PAGSASALN',
  'ITIGIL PAGSASALING',
  'TIGIL PAGSASALIN',
  'TIGIL PAGSALIN',
  'ITIGIL SALIN',
  'ITIGIL ANG PAGSASALIN',
  'ITIGIL ANG SALIN',
  'ITTIGIL PAGSASALIN',
  'ITGIL PAGSASALIN',

  // Haitian Creole
  'KANPE TRADIKSYON',
  'KANPE TRADIKSON',
  'KANPE TRADIKSION',
  'KANPE TRADIKYON',
  'KANPE TRADUKSYON',
  'KANPE TRADISYON',
  'KANP TRADIKSYON',
  'KANPE TRADIKSYO',
  'KANPE TRADUCTION',
  'KANPE TRADIK',
  'RETE TRADIKSYON',
  'RETE TRADIKSON',

  // Mandarin (Simplified Chinese) + pinyin
  '停止翻译',
  '停止 翻译',
  '关闭翻译',
  '取消翻译',
  '不要翻译',
  '别翻译',
  'TINGZHI FANYI',
  'TING ZHI FAN YI',
  'TINGZHIFANYI',
  'TINGZHI FANYII',
  'TINZHI FANYI',
  'GUANBI FANYI',
  'BUYAO FANYI',

  // Traditional Chinese
  '停止翻譯',
  '停止 翻譯',
  '關閉翻譯',
  '取消翻譯',
  '不要翻譯',
  '別翻譯',

  // Japanese + romaji
  '翻訳停止',
  '翻訳を停止',
  '翻訳 停止',
  '翻訳中止',
  '翻訳をやめて',
  '翻訳やめて',
  '翻訳オフ',
  'ほんやくていし',
  'HONYAKU TEISHI',
  "HON'YAKU TEISHI",
  'HONYAKU WO TEISHI',
  'HONYAKU O TEISHI',
  'HONYAKU TEISI',
  'HONYAKU CHUUSHI',
  'HONYAKU YAMETE',

  // Korean + romanized
  '번역 중지',
  '번역중지',
  '번역 중단',
  '번역중단',
  '번역 정지',
  '번역정지',
  '번역 끄기',
  '번역끄기',
  'BEONYEOK JUNGJI',
  'BEONYEOG JUNGJI',
  'BUNYUK JUNGJI',
  'BEONYEOK JUNGDAN',
  'BUNYUK JUNGDAN',

  // Bahasa Indonesia
  'HENTIKAN TERJEMAHAN',
  'HENTIKAN TERJEMAH',
  'HENTIKAN TERJEMAHN',
  'HENTIKAN TERJEMAAN',
  'HENTIKAN TERJEMHAN',
  'HENTIKN TERJEMAHAN',
  'HENTIKAN PENERJEMAHAN',
  'BERHENTI TERJEMAHAN',
  'BERHENTI TERJEMAH',
  'MATIKAN TERJEMAHAN',
  'STOP TERJEMAHAN',
  'STOP TERJEMAH',

  // Thai + romanized
  'หยุดแปล',
  'หยุด แปล',
  'หยุดการแปล',
  'ปิดการแปล',
  'ปิดแปล',
  'YUT PLAE',
  'YUD PLAE',
  'YUT KAN PLAE',
  'YUD KARN PLAE',
  'PIT KAN PLAE',

  // Vietnamese
  'DỪNG DỊCH',
  'DUNG DICH',
  'DỪNG DICH',
  'DỪNG BẢN DỊCH',
  'DUNG BAN DICH',
  'DỪNG DỊCH THUẬT',
  'DUNG DICH THUAT',
  'NGỪNG DỊCH',
  'NGUNG DICH',
  'TẮT DỊCH',
  'TAT DICH',
  'STOP DỊCH',
  'STOP DICH',

  // Malay
  'HENTIKAN PENTERJEMAHAN',
  'BERHENTI MENTERJEMAH',
  'TUTUP TERJEMAHAN',
  'TUTUP TERJEMAH'
];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
}

const STOP_TRANSLATE_PATTERN = new RegExp(
  `^(?:${STOP_TRANSLATE_KEYWORDS.map(escapeRegExp).join('|')})$`,
  'iu'
);

export function isStopTranslate(content: Maybe<string>): boolean {
  if (!isString(content)) {
    return false;
  }
  return STOP_TRANSLATE_PATTERN.test(content.trim());
}

export function shouldSkipTranslation(
  input:
    | { direction: 'inbound'; translationEnabled: boolean }
    | { direction: 'outbound'; driverTranslationEnabled: boolean }
): boolean {
  if (input.direction === 'inbound') {
    return input.translationEnabled === false;
  }
  return input.driverTranslationEnabled === false;
}

export function canonicalStopTranslateKeyword(locale: DriverGreetingCometLocale): string {
  return CANONICAL_STOP_TRANSLATE_KEYWORDS[locale];
}
